import { useEffect, useRef, type ReactNode } from 'react';
import { useT } from '@/i18n';
import { Link, useLocation } from 'react-router';
import { useShell } from '@/app/providers/Shell';
import { useTheme } from '@/app/providers/Theme';
import { GROUPS, PAGES, pageByPath } from '@/app/nav';
import { Icon } from '@/components/ui/Icon';
import { BellMenu } from '@/features/notices/BellMenu';
import { useAccount } from '@/hooks/queries';
import { cx } from '@/lib/format';
import { NavLock } from '@/features/plans/Upgrade';

export function TopBar({ compact = false, title }: { compact?: boolean; title?: ReactNode }) {
  const { pathname } = useLocation();
  const page = pageByPath(pathname);
  const { theme, setTheme } = useTheme();
  const { navOpen, setNavOpen } = useShell();
  const { plan } = useAccount();
  const { t } = useT();
  // phones: the five sections scroll sideways — keep the active one in view
  const pills = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = pills.current;
    const on = nav?.querySelector<HTMLElement>('.on');
    if (nav && on && nav.scrollWidth > nav.clientWidth) nav.scrollTo({ left: on.offsetLeft - (nav.clientWidth - on.offsetWidth) / 2 });
  }, [page.group]);
  return (
    <header className="top">
      <div className="top-l">
        <button className="cbtn" type="button" aria-label={t('a11y.menu')} onClick={() => setNavOpen(!navOpen)}><svg viewBox="0 0 24 24"><path d="M5 8h14M5 12h14M5 16h9" /></svg></button>
        <Link className="brand" to="/"><span className="brand-mark"><img src="/assets/brand/orca-mark.png" alt="" /></span><b>orca</b></Link>
        {title}
      </div>
      <nav className="pills" aria-label={t('nav:sections')} ref={pills}>
        {GROUPS.map(([g, label, to]) => <Link key={g} to={to} className={cx(page.group === g && 'on')} aria-current={page.group === g ? 'page' : undefined}>{t(`nav:group.${label}`)}<NavLock path={to} /></Link>)}
      </nav>
      <div className="top-r">
        <button className="cbtn sm" type="button" id={compact ? undefined : 'themeBtn'} aria-label={theme === 'light' ? t('a11y.toDark') : t('a11y.toLight')} onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
          <Icon name={theme === 'light' ? 'moon' : 'sun'} />
        </button>
        <BellMenu />
        <Link className={cx('cbtn sm', pathname === '/settings' && 'on')} to="/settings" aria-label={PAGES.settings.title}>
          <svg viewBox="0 0 24 24"><path d="M4 7h10M18 7h2M4 17h2M10 17h10" /><circle cx="16" cy="7" r="2" /><circle cx="8" cy="17" r="2" /></svg>
        </Link>
        <Link className={cx('me', pathname === '/profile' && 'on')} to="/profile"><span className="me-av"><img src="/assets/brand/orca-mark.png" alt="" /></span><span className="me-t"><b>{t('nav:myProfile')}</b><small>{plan.name}</small></span></Link>
      </div>
    </header>
  );
}
